import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Shield, FileText, X } from 'lucide-react';
import { format } from 'date-fns';
import type { Asset } from '@/types/asset';
import { CurrencyInput } from '@/components/ui/currency-input';
import { DateInputPicker } from '@/components/ui/date-input-picker';
import { useState } from 'react';
import { AssetDetailRegulatoryCompliance } from '@/components/assets/AssetDetailRegulatoryCompliance';

interface AssetDetailComplianceProps {
  readonly asset: Asset;
  readonly onAssetUpdate: (asset: Asset) => void;
  readonly isEditMode?: boolean;
}

interface TextFieldProps {
  readonly id: string;
  readonly label: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly placeholder?: string;
  readonly isEditMode: boolean;
}

function TextField({ id, label, value, onChange, placeholder, isEditMode }: Readonly<TextFieldProps>) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      {isEditMode ? (
        <Input
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
        />
      ) : (
        <Input
          id={id}
          value={value}
          readOnly
        />
      )}
    </div>
  );
}

interface TextAreaFieldProps {
  readonly id: string;
  readonly label: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly placeholder?: string;
  readonly isEditMode: boolean;
  readonly rows?: number;
}

function TextAreaField({ id, label, value, onChange, placeholder, isEditMode, rows = 4 }: Readonly<TextAreaFieldProps>) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      {isEditMode ? (
        <Textarea
          id={id}
          value={value}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={rows}
        />
      ) : (
        <Textarea
          id={id}
          value={value}
          readOnly
          rows={rows}
        />
      )}
    </div>
  );
}

interface DateFieldProps {
  readonly id: string;
  readonly label: string;
  readonly value: string | undefined;
  readonly onChange: (value: string) => void;
  readonly isEditMode: boolean;
}

function DateField({ id, label, value, onChange, isEditMode }: Readonly<DateFieldProps>) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      {isEditMode ? (
        <DateInputPicker
          date={value ? new Date(value) : undefined}
          onDateChange={(date) => onChange(date ? format(date, 'yyyy-MM-dd') : '')}
          placeholder="DD/MM/YYYY"
        />
      ) : (
        <Input
          id={id}
          value={value ? format(new Date(value), 'dd/MM/yyyy') : ''}
          readOnly
        />
      )}
    </div>
  );
}

export function AssetDetailCompliance({ asset, onAssetUpdate, isEditMode = false }: Readonly<AssetDetailComplianceProps>) {
  const [warrantyCertFile, setWarrantyCertFile] = useState<File | null>(null);

  const handleFieldChange = (field: keyof Asset, value: any) => {
    onAssetUpdate({ ...asset, [field]: value });
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setWarrantyCertFile(file);
      // In a real implementation, you would upload this file to the server
      console.log('Warranty certificate file selected:', file.name);
    }
  };

  const removeFile = () => {
    setWarrantyCertFile(null);
  };

  return (
    <div className="space-y-6">
      {/* Insurance & Warranty Information */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Insurance Information Card */}
        <Card className="border border-border/40">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Insurance Information
          </CardTitle>
          <CardDescription className="text-xs">
            Insurance coverage details and policy information
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <TextField
            id="insuranceProvider"
            label="Insurance Provider"
            value={asset.insuranceProvider ?? ''}
            onChange={(value) => handleFieldChange('insuranceProvider', value)}
            placeholder="Enter insurance provider name"
            isEditMode={isEditMode}
          />

          <TextField
            id="insurancePolicyNumber"
            label="Policy Number"
            value={asset.insurancePolicyNumber ?? ''}
            onChange={(value) => handleFieldChange('insurancePolicyNumber', value)}
            placeholder="Enter policy number"
            isEditMode={isEditMode}
          />

          <div className="space-y-2">
            <Label htmlFor="insuranceValue">Insurance Value</Label>
            {isEditMode ? (
              <CurrencyInput
                id="insuranceValue"
                value={asset.insuranceValue ?? 0}
                onChange={(value: number) => handleFieldChange('insuranceValue', value)}
              />
            ) : (
              <Input
                id="insuranceValue"
                value={asset.insuranceValue 
                  ? new Intl.NumberFormat('en-GB', { style: 'currency', currency: asset.currency ?? 'GBP' }).format(asset.insuranceValue)
                  : ''
                }
                readOnly
              />
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <DateField
              id="insuranceStartDate"
              label="Insurance Start Date"
              value={asset.insuranceStartDate}
              onChange={(value) => handleFieldChange('insuranceStartDate', value)}
              isEditMode={isEditMode}
            />

            <DateField
              id="insuranceExpiryDate"
              label="Insurance Expiry Date"
              value={asset.insuranceExpiryDate}
              onChange={(value) => handleFieldChange('insuranceExpiryDate', value)}
              isEditMode={isEditMode}
            />
          </div>

          <TextAreaField
            id="insuranceNotes"
            label="Additional Notes"
            value={asset.insuranceNotes ?? ''}
            onChange={(value) => handleFieldChange('insuranceNotes', value)}
            placeholder="Enter any additional insurance information or notes"
            isEditMode={isEditMode}
          />
        </CardContent>
      </Card>

      {/* Warranty Information Card */}
      <Card className="border border-border/40">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Warranty Information
          </CardTitle>
          <CardDescription className="text-xs">
            Warranty coverage terms and certification details
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <TextField
            id="warrantyProvider"
            label="Warranty Provider"
            value={asset.warrantyProvider ?? ''}
            onChange={(value) => handleFieldChange('warrantyProvider', value)}
            placeholder="Enter warranty provider name"
            isEditMode={isEditMode}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <DateField
              id="warrantyStartDate"
              label="Warranty Start Date"
              value={asset.warrantyStartDate}
              onChange={(value) => handleFieldChange('warrantyStartDate', value)}
              isEditMode={isEditMode}
            />

            <DateField
              id="warrantyExpiryDate"
              label="Warranty Expiry Date"
              value={asset.warrantyExpiryDate}
              onChange={(value) => handleFieldChange('warrantyExpiryDate', value)}
              isEditMode={isEditMode}
            />
          </div>

          <TextAreaField
            id="warrantyTerms"
            label="Warranty Terms"
            value={asset.warrantyTerms ?? ''}
            onChange={(value) => handleFieldChange('warrantyTerms', value)}
            placeholder="Enter warranty terms and conditions"
            isEditMode={isEditMode}
            rows={3}
          />

          <div className="space-y-2">
            <Label htmlFor="warrantyCertification">Warranty Certification</Label>
            {isEditMode ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Input
                    id="warrantyCertification"
                    type="file"
                    onChange={handleFileUpload}
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  />
                </div>
                {warrantyCertFile && (
                  <div className="flex items-center justify-between p-2 bg-gray-50 rounded border">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-gray-500" />
                      <span className="text-sm">{warrantyCertFile.name}</span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={removeFile}
                      className="h-6 w-6 p-0"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                )}
                <p className="text-xs text-muted-foreground">
                  Upload warranty certificate (PDF, JPG, PNG, DOC, DOCX)
                </p>
              </div>
            ) : (
              <Input
                id="warrantyCertification"
                value={asset.warrantyCertificationUrl ? 'Certificate uploaded' : ''}
                readOnly
              />
            )}
          </div>

          <TextAreaField
            id="warrantyNotes"
            label="Additional Notes"
            value={asset.warrantyNotes ?? ''}
            onChange={(value) => handleFieldChange('warrantyNotes', value)}
            placeholder="Enter any additional warranty information or notes"
            isEditMode={isEditMode}
          />
        </CardContent>
      </Card>
      </div>

      {/* Regulatory Compliance Section */}
      <AssetDetailRegulatoryCompliance
        assetId={asset.id}
        assetName={asset.name}
        assetStatus={asset.status}
      />
    </div>
  );
}
